import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Phone, MessageCircle, MoreHorizontal, GripVertical, Search, ChevronDown, ChevronRight, X } from "lucide-react";
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay,
} from "@dnd-kit/core";
import { endpoints } from "../lib/api";
import { waLink, openWhatsApp, templates } from "../lib/wa";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import CsvImportDialog from "../components/CsvImportDialog";
import CsvExportButton from "../components/CsvExportButton";
import { LeadHeatBadge } from "../components/ScoringBadges";
import LeadStageModals, { modalForTransition } from "../components/LeadStageModals";
import { toast } from "sonner";

const STAGES = [
  { key: "new", label: "New", tone: "bg-blue-50 text-blue-700 border-blue-100" },
  { key: "contacted", label: "Contacted", tone: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  { key: "trial_booked", label: "Trial Booked", tone: "bg-amber-50 text-amber-700 border-amber-100" },
  { key: "trial_done", label: "Trial Done", tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  { key: "converted", label: "Converted", tone: "bg-teal-50 text-teal-700 border-teal-100" },
  { key: "lost", label: "Lost", tone: "bg-rose-50 text-rose-700 border-rose-100" },
];

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "Walk-in", interest: "" });
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});  // { stageKey: true }
  const [expanded, setExpanded] = useState({});    // { stageKey: true } → show all
  const [stageModal, setStageModal] = useState(null); // { kind, lead }
  const PAGE_SIZE = 25;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.listLeads();
      setLeads(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      await endpoints.createLead(form);
      toast.success("Lead added", { description: "Welcome message queued." });
      setOpen(false);
      setForm({ name: "", phone: "", source: "Walk-in", interest: "" });
      load();
    } catch {
      toast.error("Could not add lead");
    }
  };

  const moveStage = async (id, status) => {
    // optimistic UI update
    setLeads(prev => prev.map(l => (l.id === id ? { ...l, status } : l)));
    try {
      await endpoints.updateLead(id, { status });
      toast.success(`Moved to ${STAGES.find(s => s.key === status)?.label}`);
    } catch {
      toast.error("Could not move lead");
      load();
    }
  };

  /**
   * Smart move - opens the right transition modal if the move is meaningful
   * (e.g., trial booking, trial completion, conversion). Otherwise falls
   * through to a direct status update. Used by both drag-and-drop and the
   * "Move to" dropdown menu so the UX is consistent.
   */
  const requestMove = (lead, newStatus) => {
    if (lead.status === newStatus) return;
    const modalKind = modalForTransition(lead.status, newStatus);
    if (modalKind) {
      setStageModal({ kind: modalKind, lead });
      return;
    }
    moveStage(lead.id, newStatus);
  };

  const filteredLeads = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const statusFilter = params.get("status");
    let pool = statusFilter ? leads.filter(l => l.status === statusFilter) : leads;
    const q = query.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(l =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q) ||
      (l.source || "").toLowerCase().includes(q) ||
      (l.interest || "").toLowerCase().includes(q)
    );
  }, [leads, query]);

  const columns = STAGES.map(stage => ({
    ...stage,
    items: filteredLeads.filter(l => l.status === stage.key),
  }));

  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const activeLead = leads.find(l => l.id === activeId);

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const newStatus = over.id;
    const lead = leads.find(l => l.id === active.id);
    if (!lead) return;
    requestMove(lead, newStatus);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 mb-2">Pipeline</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="leads-title">
            Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Drag a lead through the pipeline. Message on WhatsApp in one tap.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CsvExportButton kind="lead" gymName={user?.gym_name} />
          <CsvImportDialog kind="lead" onImported={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="add-lead-btn"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-4 py-2.5 rounded-lg transition-colors active:scale-[0.99]"
              >
                <Plus className="w-4 h-4" />
                Add lead
              </button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">New lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="lead-name-input"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Priya Sharma"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone (with country code)</Label>
                <Input
                  id="phone"
                  data-testid="lead-phone-input"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Walk-in", "Instagram", "Meta Ad", "Google", "Referral", "Other"].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="interest">Interest (optional)</Label>
                <Input
                  id="interest"
                  value={form.interest}
                  onChange={e => setForm({ ...form, interest: e.target.value })}
                  placeholder="e.g., Boxing"
                />
              </div>
              <DialogFooter>
                <button
                  type="submit"
                  data-testid="submit-lead-btn"
                  className="w-full bg-primary text-white font-medium py-2.5 rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Add lead
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {/* Toolbar: search + summary */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70 pointer-events-none" />
          <input
            type="text"
            data-testid="leads-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone, source…"
            className="w-full pl-9 pr-9 py-2.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-card"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear"
              data-testid="clear-search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground/70 hover:text-foreground/85 hover:bg-muted"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="text-xs text-muted-foreground whitespace-nowrap" data-testid="leads-count">
          {query ? `${filteredLeads.length} of ${leads.length}` : `${leads.length}`} lead{leads.length === 1 ? "" : "s"}
          {Object.values(collapsed).some(Boolean) && (
            <>
              {" · "}
              <button
                onClick={() => setCollapsed({})}
                className="underline hover:text-foreground"
                data-testid="expand-all-columns"
              >
                Expand all
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center">
          <p className="font-display text-xl font-medium text-foreground">No leads yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add a lead or load demo data from the sidebar.</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="flex gap-4 min-w-max pb-2 items-start">
              {columns.map(col => (
                <Column
                  key={col.key}
                  col={col}
                  onMove={(id, status) => {
                    const ld = leads.find(l => l.id === id);
                    if (ld) requestMove(ld, status);
                  }}
                  isCollapsed={!!collapsed[col.key]}
                  onToggleCollapse={() => setCollapsed(c => ({ ...c, [col.key]: !c[col.key] }))}
                  pageSize={PAGE_SIZE}
                  isExpanded={!!expanded[col.key]}
                  onToggleExpand={() => setExpanded(e => ({ ...e, [col.key]: !e[col.key] }))}
                />
              ))}
            </div>
          </div>
          <DragOverlay>
            {activeLead ? (
              <div className="w-64 bg-card border border-border rounded-lg p-3 shadow-lg rotate-2">
                <div className="text-sm font-semibold text-foreground">{activeLead.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{activeLead.phone}</div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Smart transition modals - open on Kanban drag */}
      <LeadStageModals
        modal={stageModal}
        onClose={() => setStageModal(null)}
        onChanged={load}
      />
    </div>
  );
}

function Column({ col, onMove, isCollapsed, onToggleCollapse, pageSize, isExpanded, onToggleExpand }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  const showItems = isExpanded ? col.items : col.items.slice(0, pageSize);
  const hasMore = col.items.length > pageSize;

  return (
    <div className={`shrink-0 ${isCollapsed ? "w-12" : "w-72"}`} data-testid={`column-${col.key}`}>
      {isCollapsed ? (
        <button
          onClick={onToggleCollapse}
          data-testid={`expand-${col.key}`}
          className="w-full h-[220px] bg-muted/40 hover:bg-muted border border-border rounded-xl flex flex-col items-center justify-center gap-2 text-muted-foreground transition-colors"
          title={`${col.label} (${col.items.length})`}
        >
          <ChevronRight className="w-4 h-4" />
          <span className="[writing-mode:vertical-rl] text-xs font-semibold uppercase tracking-wider">
            {col.label}
          </span>
          <span className="text-xs font-medium text-muted-foreground/70">{col.items.length}</span>
        </button>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${col.tone} whitespace-nowrap`}>
                {col.label}
              </span>
              <span className="text-xs text-muted-foreground/70">{col.items.length}</span>
            </div>
            <button
              onClick={onToggleCollapse}
              data-testid={`collapse-${col.key}`}
              aria-label="Collapse column"
              className="p-1 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
          <div
            ref={setNodeRef}
            data-testid={`dropzone-${col.key}`}
            className={`rounded-xl p-3 space-y-2 min-h-[220px] transition-colors ${
              isOver ? "bg-primary/10 ring-2 ring-primary/20" : "bg-muted/40"
            }`}
          >
            {showItems.map(lead => (
              <LeadCard key={lead.id} lead={lead} onMove={onMove} />
            ))}
            {col.items.length === 0 && (
              <div className="text-[11px] text-muted-foreground/50 text-center py-6 select-none">Drop here</div>
            )}
            {hasMore && (
              <button
                onClick={onToggleExpand}
                data-testid={`show-more-${col.key}`}
                className="w-full mt-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-card py-2 rounded-md transition-colors"
              >
                {isExpanded ? "Show fewer" : `Show all ${col.items.length}`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* Map raw source string (free-text from CSV / forms) to a semantic badge tone. */
const SOURCE_TONE = (raw) => {
  const v = String(raw || "").toLowerCase();
  if (v.includes("meta") || v.includes("facebook") || v.includes("fb")) return { label: "Meta Ads", tone: "tasks" };
  if (v.includes("insta")) return { label: "Instagram", tone: "ai" };
  if (v.includes("refer")) return { label: "Referral", tone: "growth" };
  if (v.includes("walk")) return { label: "Walk-in", tone: "ops" };
  if (v.includes("google")) return { label: "Google", tone: "leads" };
  return { label: raw || "Other", tone: "ops" };
};

const SRC_BADGE = {
  growth: "bg-growth/10 text-growth",
  leads:  "bg-leads/10  text-leads",
  trials: "bg-trials/10 text-trials",
  tasks:  "bg-tasks/10  text-tasks",
  ai:     "bg-ai/10     text-ai",
  ops:    "bg-muted     text-muted-foreground",
};

function lastActivityLabel(lead) {
  const t = lead.updated_at || lead.created_at;
  if (!t) return null;
  const diff = (Date.now() - new Date(t).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function LeadCard({ lead, onMove }) {
  const { user } = useAuth();
  const msg = templates.welcome(lead.name, user?.gym_name);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const src = SOURCE_TONE(lead.source);
  const last = lastActivityLabel(lead);
  const trialDate = lead.trial_date ? new Date(lead.trial_date) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`lead-card-${lead.id}`}
      className={`bg-card border border-border rounded-xl p-3.5 hover:border-foreground/15 hover:shadow-sm transition-all ${
        isDragging ? "opacity-40" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <button
          {...listeners}
          {...attributes}
          data-testid={`drag-handle-${lead.id}`}
          aria-label="Drag to move"
          className="p-1 -ml-1 mt-0.5 text-muted-foreground/40 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <Link
          to={`/leads/${lead.id}`}
          className="flex-1 min-w-0"
          data-testid={`lead-link-${lead.id}`}
        >
          <div className="text-sm font-semibold text-foreground truncate">{lead.name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Phone className="w-3 h-3" /> {lead.phone}
          </div>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-muted" aria-label="More">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {STAGES.filter(s => s.key !== lead.status).map(s => (
              <DropdownMenuItem key={s.key} onClick={() => onMove(lead.id, s.key)}>
                Move to {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Source + heat (temperature) row */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2.5" data-testid={`lead-meta-${lead.id}`}>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${SRC_BADGE[src.tone]}`}>
          {src.label}
        </span>
        <LeadHeatBadge lead={lead} size="sm" />
        {trialDate && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-trials/10 text-trials">
            Trial {trialDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
        )}
      </div>

      {/* Footer: last activity + WhatsApp */}
      <div className="flex items-center justify-between gap-2">
        {last && <span className="text-[10.5px] text-muted-foreground/80">Active {last}</span>}
        <a
          href={waLink(lead.phone, msg)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid={`lead-wa-${lead.id}`}
          className="ml-auto inline-flex items-center gap-1 bg-[#25D366] hover:bg-[#1EBE5D] text-white text-xs font-medium px-2.5 py-1 rounded-md transition-colors active:scale-95"
        >
          <MessageCircle className="w-3 h-3" />
          WhatsApp
        </a>
      </div>
    </div>
  );
}
